'use client';

import { features } from "@/be/adapters/features";
import { updateResource_testingTags } from "@/be/commands/system-commands";
import { CLM } from "@/common/schema/claims";
import { toRawUpdateResource_TestingTags, UpdateResource_TestingTags } from "@/common/schema/commands/System/UpdateResource_TestingTags";
import { SYS } from "@/common/schema/system";
import { unexpectedCodePath } from "@/fx/data-helpers";
import { Id } from "@/fx/Id";
import { Pill } from "@/ui/controls/buttons";
import assert from "assert";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

export function TestingTagsSelector({BOOM_ENV, claim, userId, userTestingTags: userTestingTags_}: {
  BOOM_ENV:         string,
  claim?:           CLM.Claim,
  userId?:          string,
  userTestingTags?: string[],
}) {
  const FT = features({BOOM_ENV});

  if(!FT.hud.testingTags()) return undefined;

  const userTestingTags = userTestingTags_?.map(SYS.testingTag);

  const tagGroups = [
    SYS.ResourceType.SYSTEM_USER,
    SYS.ResourceType.CLAIMS_CLAIM,
  ];

  return <>
    {tagGroups.map(_ => <TagsGroupPills key={_.identifier} claim={claim} userId={userId} userTestingTags={userTestingTags} resourceType={_} />)}
  </>
}

function TagsGroupPills({ claim, userId, userTestingTags, resourceType }: {
  claim?:           CLM.Claim,
  userId?:          string,
  userTestingTags?: SYS.TestingTag[],
  resourceType:     SYS.ResourceType,
}) {
  const { update } = useSession();
  const router = useRouter();

  const onToggle = ({ testingTag, resourceType }: {
    testingTag: SYS.TestingTag,
    resourceType: SYS.ResourceType
  }) => {
    const tagsWithToggled = (testingTags: SYS.TestingTag[] = []) => {
      return testingTags.includes(testingTag) ? testingTags.filter((_) => _ !== testingTag)
                                              : [...testingTags, testingTag];
    }

    switch(resourceType) {
      case SYS.ResourceType.CLAIMS_CLAIM:
        assert(claim !== undefined, "Claim must be defined to update testing tags on a claim");
        updateResourceTestingTags({
          resourceId:  claim.id,
          testingTags: tagsWithToggled(claim.testingTags),
        });
        break;
      case SYS.ResourceType.SYSTEM_USER:
        assert(userId !== undefined, "User Id must be defined to update testing tags on a user");
        updateResourceTestingTags({
          resourceId:  Id.from(userId.toString()),
          testingTags: tagsWithToggled(userTestingTags),
          onComplete: () => update(),
        });
        break;
      default: unexpectedCodePath(`Unexpected resource type: ${resourceType}`);
    }
  }

  const updateResourceTestingTags = ({ resourceId, testingTags, onComplete }: {
    resourceId:  Id,
    testingTags: SYS.TestingTag[],
    onComplete?: () => void,
  }) => {
    const command: UpdateResource_TestingTags = {
      id:          resourceId,
      testingTags: testingTags,
    };
    updateResource_testingTags({
      command: toRawUpdateResource_TestingTags(command)
    })
    .then(() => onComplete?.())
    .then(() => router.refresh());
  }

  const shouldShowTestingTag = (resourceType: SYS.ResourceType, testingTag: SYS.TestingTag) => {
    return resourceType === SYS.ResourceType.SYSTEM_USER
       || (resourceType === SYS.ResourceType.CLAIMS_CLAIM && claim !== undefined && CLM.isSafeToToggleTestingTag({ claim, testingTag }));
  }

  const selected = ({ resourceType, testingTag }: {
    resourceType: SYS.ResourceType,
    testingTag:   SYS.TestingTag,
  }): boolean => {
    const isTestingTagIn = (testingTags: SYS.TestingTag[] | undefined) => {
      return testingTags !== undefined && testingTags.includes(testingTag);
    }
    switch(resourceType) {
      case SYS.ResourceType.CLAIMS_CLAIM: return claim !== undefined && isTestingTagIn(claim.testingTags);
      case SYS.ResourceType.SYSTEM_USER:  return                        isTestingTagIn(userTestingTags  );

      default: unexpectedCodePath(`Unexpected resource type: ${resourceType}`);
    }
  }

  const testingTagsToShow = SYS.testingTags.filter(_ =>
    _.resourceTypes.includes(resourceType) &&
    shouldShowTestingTag(resourceType, _)
  );
  const showTagGroup = testingTagsToShow.length > 0;

  return <>
    {showTagGroup &&
      <div className="flex-none rounded-md text-sm font-medium pb-2" key={resourceType.identifier}>
        {resourceType.name} Testing Tags
        <div className="flex flex-wrap rounded-md text-lg font-medium pb-2">
          {testingTagsToShow.map(testingTag =>
            <TagGroupPill key={testingTag.identifier}
                          testingTag={testingTag}
                          selected={selected({ resourceType, testingTag })}
                          onToggle={() => onToggle({ resourceType, testingTag })} />)}
        </div>
      </div>}
  </>
}

function TagGroupPill({ testingTag, selected, onToggle }: {
  testingTag: SYS.TestingTag,
  selected:   boolean,
  onToggle:   () => void,
}) {
  return <>
    <div className="w-1/3 px-1 mb-2">
      <Pill className="mr-0 px-0" onClick={onToggle} selected={selected}>
        {testingTag.shortName}
      </Pill>
    </div>
  </>;
}
